import { Box, ButtonGroup, Button, Typography, useTheme, LinearProgress, Switch, FormControlLabel, Card, CardContent, Chip, Skeleton, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { generatePDF, calculatePercentageChange } from '../../../dwi-functions';
import { useContext, useEffect, useRef, useState, useMemo } from "react";
import { AppVarsContext } from '../../../context/AppVarsContext';
import { FcPrint } from 'react-icons/fc';
import ChartCard from '../../../report-components/stackadapt-chart-card';
import TrendChart from '../../../report-components/trend-chart';
import { useToken } from '../../../Login/TokenProvider';
import TableCard from '../../../system/table-card';
import ViMarcTableCard from '../../../system/vimarc-table-card';
import CreativeDetailTabs from '../CreativeDetailsTab';
import CloseIcon from '@mui/icons-material/Close';
import { getReportSurfaceSx } from '../../shared/report-design';
import StackAdaptSpendStrip from '../channel-spend-strip';
import { getChannelTrendData } from '../trend-data';
import { buildChannelMetricBundle } from '../channel-metrics';
import { resolveShowCost } from '../../../system/report-visibility';
import { formatUsd } from '../../../system/report-formatters';



const safeNum = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const getMetricDelta = (metric, previousMetric) => {
    const explicitDelta = Number(metric?.delta);
    if (metric?.delta != null && Number.isFinite(explicitDelta)) return explicitDelta;
    return calculatePercentageChange(safeNum(metric?.count), safeNum(previousMetric?.count));
};

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(safeNum(value));
const formatPercent = (value) => `${safeNum(value).toFixed(2)}%`;
const formatCurrency = (value) => formatUsd(safeNum(value));

const isLikelyUrl = (value) => {
    if (!value) return false;
    const text = String(value).trim();
    return /^https?:\/\//i.test(text);
};

const collectUrlStrings = (obj) => {
    const urls = [];
    if (!obj || typeof obj !== 'object') return urls;
    Object.values(obj).forEach((val) => {
        if (isLikelyUrl(val)) {
            urls.push(String(val));
            return;
        }
        if (Array.isArray(val)) {
            val.forEach((item) => {
                if (isLikelyUrl(item)) urls.push(String(item));
            });
        }
    });
    return urls;
};

const normalizeAssetUrls = (source) => {
    const output = [];
    const push = (val) => {
        if (val == null) return;
        if (Array.isArray(val)) {
            val.forEach((item) => push(item));
            return;
        }
        if (typeof val === 'object') {
            const url =
                val.IMAGE_ASSET_URL ||
                val.IMAGE_URL ||
                val.THUMBNAIL_URL ||
                val.thumbnail_url ||
                val.thumbnail ||
                val.ASSET_URL ||
                val.asset_url ||
                val.PREVIEW_URL ||
                val.preview_url ||
                val.CREATIVE_URL ||
                val.URL ||
                val.url ||
                val.image_url ||
                val.image ||
                val.name;
            if (isLikelyUrl(url)) output.push(String(url));
            if (isLikelyUrl(val.name)) output.push(String(val.name));
            collectUrlStrings(val).forEach((urlItem) => output.push(urlItem));
            return;
        }
        if (isLikelyUrl(val)) output.push(String(val));
    };
    push(source);
    return output.filter(Boolean);
};

const getAssetPreviewUrl = (row) => {
    const urls = normalizeAssetUrls([
        row?.IMAGE_ASSET_URL,
        row?.IMAGE_URL,
        row?.IMAGE_URLS,
        row?.ASSET_URL,
        row?.ASSET_URLS,
        row?.CREATIVE_URL,
        row?.THUMBNAIL_URL,
        row?.thumbnail,
        row?.PREVIEW_URL,
        row?.URL,
        row?.url,
        row?.ASSETS,
        row?.ASSET,
        row?.creative,
        row?.CREATIVE
    ]);
    if (urls.length) return urls[0];
    const looseUrls = collectUrlStrings(row);
    return looseUrls[0] || '';
};

const ChannelLoadingSkeleton = ({ theme }) => {
    const surfaceCardSx = getReportSurfaceSx(theme);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
            <Box sx={{ ...surfaceCardSx, p: 1.25 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Array.from({ length: 8 }).map((_, index) => (
                        <Skeleton
                            key={`stackadapt-native-skel-${index}`}
                            variant="rectangular"
                            height={90}
                            sx={{ flex: '1 1 160px', borderRadius: 2 }}
                        />
                    ))}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ ...surfaceCardSx, flex: '1 1 320px', p: 1.25 }}>
                    <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
                </Box>
                <Box sx={{ ...surfaceCardSx, flex: '1 1 320px', p: 1.25 }}>
                    <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
                </Box>
            </Box>
            <Box sx={{ ...surfaceCardSx, p: 1.25 }}>
                <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
            </Box>
        </Box>
    );
};





const Native = (props) => {
    const pDate = new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: "UTC" }).format(new Date(sessionStorage.getItem('sDate'))) + "-" + new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: "UTC" }).format(new Date(sessionStorage.getItem('eDate')));
    const theme = useTheme();
    const { appVars, appLoading } = useContext(AppVarsContext);
    const reportRef1 = useRef()
    const token = useToken();
    const admintoken = token.token.token;
    const [showTrend, setShowTrend] = useState(true);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSrc, setLightboxSrc] = useState('');
    const [lightboxTitle, setLightboxTitle] = useState('');

    const openLightbox = (src, title) => {
        if (!src) return;
        setLightboxSrc(src);
        setLightboxTitle(title || 'Creative Preview');
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        setLightboxSrc('');
        setLightboxTitle('');
    };

    //console.log('Properties', props.reportProfile)



    async function fetchData() {

    }



    useEffect(() => {


        if (!appLoading) {
            fetchData();

        }

        return () => {

        };
    }, [appLoading]);

    const trendData = getChannelTrendData(props.data, 'native');
    const awarenessMode = Boolean(props.awarenessMode || props.retailMode || props.vimarcMode);
    const ReportTableCard = awarenessMode ? ViMarcTableCard : TableCard;
    const showCost = resolveShowCost(props.reportProfile);
    const showRevenue = !awarenessMode;
    const surfaceCardSx = useMemo(() => getReportSurfaceSx(theme), [theme]);
    const statTileSx = useMemo(() => ({
        ...surfaceCardSx,
        display: 'flex',
        flex: 1,
        color: theme.palette.primary.main,
        gap: 1,
        p: 1.25,
        alignItems: 'center',
        justifyContent: 'space-between'
    }), [surfaceCardSx, theme]);
    const sectionCardSx = useMemo(() => ({
        ...surfaceCardSx,
        p: { xs: 1.25, sm: 1.5 }
    }), [surfaceCardSx]);
    const campaignRows = useMemo(() => {
        const rows = props?.campaignData?.native?.campaigns || props?.data?.channel?.native?.campaigns || [];
        return Array.isArray(rows) ? rows : [];
    }, [props.campaignData, props.data]);
    const previousCampaignRows = useMemo(() => {
        const rows = props?.data?.previousChannel?.native?.campaigns || [];
        return Array.isArray(rows) ? rows : [];
    }, [props.data]);
    const channelMetrics = useMemo(() => buildChannelMetricBundle({
        channel: props?.data?.channel?.native,
        previousChannel: props?.data?.previousChannel?.native,
        campaignRows,
        previousCampaignRows
    }), [campaignRows, previousCampaignRows, props.data]);

    const buildMetrics = (row) => {
        const impressions = safeNum(row?.IMPRESSIONS ?? row?.impressions);
        const clicks = safeNum(row?.CLICKS ?? row?.clicks);
        const conversions = safeNum(row?.CONVERSIONS ?? row?.conversions);
        const cost = safeNum(row?.COST ?? row?.cost);
        const revenue = safeNum(row?.REVENUE ?? row?.revenue);
        const ctr = safeNum(row?.CTR ?? row?.ctr ?? (impressions ? (clicks / impressions) * 100 : 0));
        const convRate = safeNum(row?.CONV_RATE ?? row?.CONVRATE ?? row?.convrate ?? (clicks ? (conversions / clicks) * 100 : 0));
        const avgGift = safeNum(row?.AVG_GIFT ?? row?.avgGift ?? row?.avg_gift ?? (conversions ? revenue / conversions : 0));
        const roas = safeNum(row?.ROAS ?? row?.roas ?? (cost ? revenue / cost : 0));
        return { impressions, clicks, conversions, cost, revenue, ctr, convRate, avgGift, roas };
    };

    const topGroupByCtr = useMemo(() => {
        const sorted = campaignRows
            .map((row) => ({ row, metrics: buildMetrics(row) }))
            .filter((item) => item.metrics.impressions > 0 || item.metrics.clicks > 0)
            .sort((a, b) => b.metrics.ctr - a.metrics.ctr);
        return sorted[0] || null;
    }, [campaignRows]);

    const topGroupByConvRate = useMemo(() => {
        const sorted = campaignRows
            .map((row) => ({ row, metrics: buildMetrics(row) }))
            .filter((item) => item.metrics.clicks > 0)
            .sort((a, b) => b.metrics.convRate - a.metrics.convRate);
        return sorted[0] || null;
    }, [campaignRows]);

    const topGroupByImpressions = useMemo(() => {
        const sorted = campaignRows
            .map((row) => ({ row, metrics: buildMetrics(row) }))
            .filter((item) => item.metrics.impressions > 0)
            .sort((a, b) => b.metrics.impressions - a.metrics.impressions);
        return sorted[0] || null;
    }, [campaignRows]);

    const pickIdValue = (...values) => (
        values.find((value) => (
            (typeof value === 'string' && value.trim() !== '') || typeof value === 'number'
        )) || null
    );

    const getGroupId = (row) => pickIdValue(
        row?.ad_group_id,
        row?.adgroup_id,
        row?.adGroupId,
        row?.adgroupId,
        row?.AD_GROUP_ID,
        row?.ADGROUP_ID,
        row?.GROUP_ID,
        row?.group_id,
        row?.groupId,
        row?.id,
        row?.ID,
        row?.campaign_id,
        row?.campaignId,
        row?.campaignID
    );

    const topGroupIds = useMemo(() => {
        const ids = [
            getGroupId(topGroupByCtr?.row),
            getGroupId((awarenessMode ? topGroupByImpressions : topGroupByConvRate)?.row)
        ].filter(Boolean);
        return Array.from(new Set(ids));
    }, [awarenessMode, topGroupByCtr, topGroupByConvRate, topGroupByImpressions]);

    const [creativeMap, setCreativeMap] = useState({});

    useEffect(() => {
        if (!appVars?.api_url) return;
        if (!topGroupIds.length) return;
        const missing = topGroupIds.filter((id) => !creativeMap[id]);
        if (!missing.length) return;
        let isActive = true;
        const fetchCreatives = async () => {
            const results = await Promise.all(
                missing.map(async (id) => {
                    try {
                        const raw = JSON.stringify({
                            campaign: id,
                            cid: sessionStorage.getItem('rid'),
                            sdate: sessionStorage.getItem('sDate'),
                            edate: sessionStorage.getItem('eDate')
                        });
                        const res = await fetch(`${appVars.api_url}/stackadapt/creatives/`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: raw,
                            redirect: 'follow'
                        });
                        const data = await res.json();
                        return [id, Array.isArray(data) ? data : []];
                    } catch (error) {
                        return [id, []];
                    }
                })
            );
            if (!isActive) return;
            setCreativeMap((prev) => {
                const next = { ...prev };
                results.forEach(([id, rows]) => {
                    next[id] = rows;
                });
                return next;
            });
        };
        fetchCreatives();
        return () => {
            isActive = false;
        };
    }, [topGroupIds, appVars?.api_url, creativeMap]);

    const getCreativeWinnerForGroup = (row, metricKey) => {
        const id = getGroupId(row);
        const creatives = id ? creativeMap[id] || [] : [];
        if (!creatives.length) return null;
        const scored = creatives
            .map((creative) => ({ creative, metrics: buildMetrics(creative) }))
            .filter((item) => item.metrics.impressions > 0 || item.metrics.clicks > 0);
        if (!scored.length) {
            return { creative: creatives[0], metrics: buildMetrics(creatives[0]) };
        }
        const key = metricKey === 'convRate' ? 'convRate' : (metricKey === 'impressions' ? 'impressions' : 'ctr');
        scored.sort((a, b) => b.metrics[key] - a.metrics[key]);
        return scored[0];
    };

    const topConvGroup = awarenessMode ? topGroupByImpressions : topGroupByConvRate;
    const topCtrWinner = useMemo(() => (
        topGroupByCtr ? getCreativeWinnerForGroup(topGroupByCtr.row, 'ctr') : null
    ), [topGroupByCtr, creativeMap]);

    const topConvWinner = useMemo(() => (
        topConvGroup ? getCreativeWinnerForGroup(topConvGroup.row, awarenessMode ? 'impressions' : 'convRate') : null
    ), [awarenessMode, topConvGroup, creativeMap]);

    const ctrGroupId = topGroupByCtr ? getGroupId(topGroupByCtr.row) : null;
    const convGroupId = topConvGroup ? getGroupId(topConvGroup.row) : null;
    const ctrCreatives = ctrGroupId ? creativeMap[ctrGroupId] : null;
    const convCreatives = convGroupId ? creativeMap[convGroupId] : null;

    const getAdLabel = (row) => {
        const label = row?.ad_group_name || row?.adgroup_name || row?.group_name || row?.GROUP_NAME || row?.name || row?.NAME || row?.campaign || row?.CAMPAIGN || row?.label || row?.AD_NAME;
        if (isLikelyUrl(label)) return 'Ad Group';
        return label || 'Ad Group';
    };
    const getCreativeLabel = (row) => {
        const label = row?.creative || row?.CREATIVE || row?.CREATIVE_NAME || row?.NAME || row?.name || row?.AD_NAME || row?.AD_ID || row?.id;
        if (isLikelyUrl(label)) return 'Creative';
        return label || 'Creative';
    };



    return (
        <>
            {props.loading ? <ChannelLoadingSkeleton theme={theme} /> : (
            (props?.data?.channel?.native ?
                <>
                <StackAdaptSpendStrip
                    showCost={showCost}
                    showRevenue={showRevenue}
                    title={props.title}
                    cost={channelMetrics.currentStats.cost.count}
                    revenue={channelMetrics.currentStats.revenue.count}
                    tileSx={statTileSx}
                />

                <Box ref={reportRef1} style={{ padding: theme.spacing(0) }}>



                    {/* Impressions */}
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, mt: 1 }} >

                        <ChartCard reportStyle
                            title="Impressions"
                            explain={JSON.parse(appVars.tooltips).impression}
                            data={channelMetrics.currentStats.impressions}
                            previousdata={channelMetrics.previousStats.impressions}
                            delta={getMetricDelta(channelMetrics.currentStats.impressions, channelMetrics.previousStats.impressions)}
                            themeColor={{
                                primary: theme.palette.primary.main,
                                secondary: theme.palette.primary.main
                            }}
                            view="stackadapt"
                            hideChart={true}
                        />

                        <ChartCard reportStyle
                            title="Clicks"
                            explain={JSON.parse(appVars.tooltips).click}
                            data={channelMetrics.currentStats.clicks}
                            previousdata={channelMetrics.previousStats.clicks}
                            delta={getMetricDelta(channelMetrics.currentStats.clicks, channelMetrics.previousStats.clicks)}
                            themeColor={{
                                primary: theme.palette.primary.main,
                                secondary: theme.palette.primary.main
                            }}

                            view="stackadapt"
                            hideChart={true}
                        />

                        <ChartCard reportStyle
                            title="CTR"
                            explain={JSON.parse(appVars.tooltips).ctr}
                            data={channelMetrics.currentStats.ctr}
                            previousdata={channelMetrics.previousStats.ctr}
                            delta={getMetricDelta(channelMetrics.currentStats.ctr, channelMetrics.previousStats.ctr)}
                            themeColor={{
                                primary: theme.palette.primary.main,
                                secondary: theme.palette.primary.main
                            }}

                            view="stackadapt"
                            hideChart={true}
                        />

                        {showCost && (
                            <ChartCard reportStyle
                                title="CPM"
                                explain={JSON.parse(appVars.tooltips).cpm}
                                data={channelMetrics.currentStats.cpm}
                                previousdata={channelMetrics.previousStats.cpm}
                                delta={getMetricDelta(channelMetrics.currentStats.cpm, channelMetrics.previousStats.cpm)}
                                themeColor={{
                                    primary: theme.palette.primary.main,
                                    secondary: theme.palette.primary.main
                                }}

                                view="stackadapt"
                                hideChart={true}
                            />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, mt: 1 }} >
                        <ChartCard reportStyle
                            title="Conversions"
                            explain={JSON.parse(appVars.tooltips).conversion}
                            data={channelMetrics.currentStats.conversions}
                            previousdata={channelMetrics.previousStats.conversions}
                            delta={getMetricDelta(channelMetrics.currentStats.conversions, channelMetrics.previousStats.conversions)}
                            themeColor={{
                                primary: theme.palette.primary.main,
                                secondary: theme.palette.primary.main
                            }}

                            view="stackadapt"
                            hideChart={true}
                        />

                        {showRevenue && (
                            <ChartCard reportStyle
                                title="Revenue"
                                explain={JSON.parse(appVars.tooltips).revenue}
                                data={channelMetrics.currentStats.revenue}
                                previousdata={channelMetrics.previousStats.revenue}
                                delta={getMetricDelta(channelMetrics.currentStats.revenue, channelMetrics.previousStats.revenue)}
                                themeColor={{
                                    primary: theme.palette.primary.main,
                                    secondary: theme.palette.primary.main
                                }}

                                view="stackadapt"
                                hideChart={true}
                            />
                        )}

                        <ChartCard reportStyle
                            title="Conversion Rate"
                            explain={JSON.parse(appVars.tooltips).conv_rate}
                            data={channelMetrics.currentStats.convrate}
                            previousdata={channelMetrics.previousStats.convrate}
                            delta={getMetricDelta(channelMetrics.currentStats.convrate, channelMetrics.previousStats.convrate)}
                            themeColor={{
                                primary: theme.palette.primary.main,
                                secondary: theme.palette.primary.main
                            }}

                            view="stackadapt"
                            hideChart={true}
                        />

                        {showRevenue && (
                            <ChartCard reportStyle
                                title="Avg Transaction"
                                explain={JSON.parse(appVars.tooltips).avg_gift}
                                data={channelMetrics.currentStats.avgGift}
                                previousdata={channelMetrics.previousStats.avgGift}
                                delta={getMetricDelta(channelMetrics.currentStats.avgGift, channelMetrics.previousStats.avgGift)}
                                themeColor={{
                                    primary: theme.palette.primary.main,
                                    secondary: theme.palette.primary.main
                                }}

                                view="stackadapt"
                                hideChart={true}
                            />
                        )}

                        {showCost && (
                            <ChartCard reportStyle
                                title="ROAS"
                                explain={JSON.parse(appVars.tooltips).roas || JSON.parse(appVars.tooltips).roi || ''}
                                data={channelMetrics.currentStats.roas}
                                previousdata={channelMetrics.previousStats.roas}
                                delta={getMetricDelta(channelMetrics.currentStats.roas, channelMetrics.previousStats.roas)}
                                themeColor={{
                                    primary: theme.palette.primary.main,
                                    secondary: theme.palette.primary.main
                                }}

                                view="stackadapt"
                                hideChart={true}
                            />
                        )}

                    </Box>

                    {(topGroupByCtr || topConvGroup) && (
                        <Box sx={{ mt: 1 }}>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', lg: 'calc(50% - 16px)' } }}>
                                    <Card variant="outlined" sx={surfaceCardSx}>
                                        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Top Ads by CTR
                                            </Typography>
                                            {topCtrWinner ? (
                                                <Box
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                        p: 1.5,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            flexDirection: { xs: 'column', sm: 'row' },
                                                            alignItems: 'stretch'
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 1,
                                                                overflow: 'hidden',
                                                                minHeight: 100,
                                                                background: '#fff',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flex: { xs: '0 0 auto', sm: '0 0 140px' },
                                                                width: { xs: '100%', sm: 140 },
                                                                cursor: getAssetPreviewUrl(topCtrWinner.creative) ? 'zoom-in' : 'default'
                                                            }}
                                                            onClick={() => openLightbox(getAssetPreviewUrl(topCtrWinner.creative), getCreativeLabel(topCtrWinner.creative))}
                                                        >
                                                            {getAssetPreviewUrl(topCtrWinner.creative) ? (
                                                                <Box
                                                                    component="img"
                                                                    src={getAssetPreviewUrl(topCtrWinner.creative)}
                                                                    alt={`Asset preview for ${getCreativeLabel(topCtrWinner.creative)}`}
                                                                    loading="lazy"
                                                                    onError={(event) => {
                                                                        event.currentTarget.src = 'https://api.digitalwerks.com/imageFiles/no-image-icon.png';
                                                                    }}
                                                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                />
                                                            ) : (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    No preview
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    CTR {formatPercent(topCtrWinner.metrics.ctr)}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {formatNumber(topCtrWinner.metrics.clicks)} clicks
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                {getCreativeLabel(topCtrWinner.creative)}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Group: {getAdLabel(topGroupByCtr?.row)} - {props.title}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                                <Chip size="small" label={`Impr ${formatNumber(topCtrWinner.metrics.impressions)}`} />
                                                                <Chip size="small" label={`Conv ${formatNumber(topCtrWinner.metrics.conversions)}`} />
                                                                {showCost ? <Chip size="small" label={`Spend ${formatCurrency(topCtrWinner.metrics.cost)}`} /> : null}
                                                                {showRevenue ? <Chip size="small" label={`Revenue ${formatCurrency(topCtrWinner.metrics.revenue)}`} /> : null}
                                                                {showCost ? <Chip size="small" label={`ROAS ${topCtrWinner.metrics.roas.toFixed(2)}x`} /> : null}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    {topGroupByCtr
                                                        ? (ctrCreatives === undefined ? 'Loading creative details...' : 'No creative data available.')
                                                        : 'No CTR data available.'}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Box>
                                <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', lg: 'calc(50% - 16px)' } }}>
                                    <Card variant="outlined" sx={surfaceCardSx}>
                                        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                {awarenessMode ? 'Top Ads by Impressions' : 'Top Ads by Conversion Rate'}
                                            </Typography>
                                            {topConvWinner ? (
                                                <Box
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                        p: 1.5,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            flexDirection: { xs: 'column', sm: 'row' },
                                                            alignItems: 'stretch'
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 1,
                                                                overflow: 'hidden',
                                                                minHeight: 100,
                                                                background: '#fff',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flex: { xs: '0 0 auto', sm: '0 0 140px' },
                                                                width: { xs: '100%', sm: 140 },
                                                                cursor: getAssetPreviewUrl(topConvWinner.creative) ? 'zoom-in' : 'default'
                                                            }}
                                                            onClick={() => openLightbox(getAssetPreviewUrl(topConvWinner.creative), getCreativeLabel(topConvWinner.creative))}
                                                        >
                                                            {getAssetPreviewUrl(topConvWinner.creative) ? (
                                                                <Box
                                                                    component="img"
                                                                    src={getAssetPreviewUrl(topConvWinner.creative)}
                                                                    alt={`Asset preview for ${getCreativeLabel(topConvWinner.creative)}`}
                                                                    loading="lazy"
                                                                    onError={(event) => {
                                                                        event.currentTarget.src = 'https://api.digitalwerks.com/imageFiles/no-image-icon.png';
                                                                    }}
                                                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                />
                                                            ) : (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    No preview
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {awarenessMode ? `Impr ${formatNumber(topConvWinner.metrics.impressions)}` : `Conv ${formatPercent(topConvWinner.metrics.convRate)}`}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {awarenessMode ? `${formatNumber(topConvWinner.metrics.clicks)} clicks` : `${formatNumber(topConvWinner.metrics.conversions)} conv`}
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                {getCreativeLabel(topConvWinner.creative)}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Group: {getAdLabel(topConvGroup?.row)} - {props.title}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                                <Chip size="small" label={`Clicks ${formatNumber(topConvWinner.metrics.clicks)}`} />
                                                                <Chip size="small" label={`Impr ${formatNumber(topConvWinner.metrics.impressions)}`} />
                                                                {showRevenue ? (
                                                                    <>
                                                                        <Chip size="small" label={`Revenue ${formatCurrency(topConvWinner.metrics.revenue)}`} />
                                                                        <Chip size="small" label={`Avg ${formatCurrency(topConvWinner.metrics.avgGift)}`} />
                                                                    </>
                                                                ) : null}
                                                                {showCost ? <Chip size="small" label={`ROAS ${topConvWinner.metrics.roas.toFixed(2)}x`} /> : null}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    {topGroupByConvRate
                                                        ? (convCreatives === undefined ? 'Loading creative details...' : 'No creative data available.')
                                                        : (awarenessMode ? 'No impression data available.' : 'No conversion rate data available.')}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {trendData && Array.isArray(trendData) && trendData.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <FormControlLabel
                                control={<Switch size="small" checked={showTrend} onChange={(event) => setShowTrend(event.target.checked)} />}
                                label="Trend"
                            />
                        </Box>
                    )}

                    {trendData && Array.isArray(trendData) && trendData.length > 0 && showTrend && (
                        <Box sx={{ mt: 1 }}>
                            <Box sx={sectionCardSx}>
                                <TrendChart reportStyle trendData={trendData} showCost={showCost} showRevenue={showRevenue} noteType="stackadapt" />
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {(props.campaignData ?
                                <Box sx={{ flexGrow: 1, flexBasis: '100%', minWidth: 0 }}>
                                    <ReportTableCard branded={false}
                                        title="Campaigns"
                                        data={props.campaignData.native.campaigns}
                                        count={(props.campaignData.native.campaigns.length > 10 ? 10 : props.campaignData.native.campaigns.length)}

                                        reportProfile={props.reportProfile}
                                        themeColor={{
                                            primary: theme.palette.primary.main,
                                            secondary: theme.palette.primary.main
                                        }}
                                        columns={[
                                            {
                                                header: "Campaign",
                                                field: "name",
                                                grow: true,
                                                minSize: 300,
                                                Cell: ({ cell }) => {
                                                    //console.log('Cell', cell.row.original);
                                                    return (
                                                        <Typography textAlign={'right'} fontSize={13} fontWeight={900} >{cell.row.original.name}</Typography>
                                                    )
                                                },
                                                Footer: () => <Typography fontSize={13} fontWeight={900} textAlign={'right'}>Totals</Typography>,
                                            }]}

                                        renderDetailPanel={({ row }) => (
                                            <CreativeDetailTabs
                                                rowData={row.original}
                                                appVars={appVars}
                                                sDate={props.sDate}
                                                eDate={props.eDate}
                                                reportProfile={props.reportProfile}
                                                showRevenue={showRevenue}
                                            />
                                        )}
                                    />
                                </Box>
                                : <Box sx={sectionCardSx}><Typography variant='h6' align='center'>Loading Campaign Data</Typography><LinearProgress color='primary' sx={{ margin: theme.spacing(3) }}></LinearProgress></Box>
                            )}
                        </Box>
                    </Box>


                </Box>
            </>
            : <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
                <Box sx={{ ...surfaceCardSx, justifyContent: 'center', alignItems: 'center', p: theme.spacing(2.5), minWidth: 260 }}>
                    <Typography align="center" gutterBottom>No {props.title} Data</Typography>
                </Box>
            </Box>
            ))}

            <Dialog open={lightboxOpen} onClose={closeLightbox} maxWidth="md" fullWidth PaperProps={{ sx: surfaceCardSx }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: theme.palette.background.paper, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {lightboxTitle}
                    </Typography>
                    <IconButton onClick={closeLightbox} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {lightboxSrc ? (
                        <Box
                            component="img"
                            src={lightboxSrc}
                            alt={lightboxTitle}
                            sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                            onError={(event) => {
                                event.currentTarget.src = 'https://api.digitalwerks.com/imageFiles/no-image-icon.png';
                            }}
                        />
                    ) : null}
                </DialogContent>
            </Dialog>
        </>
    );
};

Native.propTypes = {

};

export default Native;
